import { scaleValue } from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type PredictiveSearch } from '@/scripts/optional/predictive-search'
import { type DetailsModal } from '@/scripts/theme/details-modal'
import { type HeaderMenu } from '@/scripts/theme/header-menu'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { SELECTORS } from '@/scripts/core/global'

export class StickyHeader extends UcoastEl {
	static htmlSelector = 'sticky-header'
	preventHide: boolean = false
	preventReveal: boolean = false
	header: HTMLElement
	headerIsAlwaysSticky?: boolean
	headerBounds: DOMRect | {} = {}
	currentScrollTop: number = 0
	predictiveSearch?: PredictiveSearch
	onScrollHandler!: (event: Event) => void
	hideHeaderOnScrollUp!: () => void
	searchModal?: DetailsModal
	isScrolling?: number
	disclosures?: [] | NodeListOf<HeaderMenu>
	setHeaderHeightHandler?: () => void
	headerResizeObserver?: ResizeObserver
	headerMediaQuery?: MediaQueryList
	constructor() {
		super()
		this.header = q.rs(SELECTORS.sectionHeader)
	}

	override connectedCallback() {
		this.header = q.rs(SELECTORS.sectionHeader)
		this.headerIsAlwaysSticky =
			this.getAttribute('data-sticky-type') === 'always' ||
			this.getAttribute('data-sticky-type') === 'reduce-logo-size'
		this.headerBounds = {}

		this.setHeaderHeight()

		this.setHeaderHeightHandler = this.setHeaderHeight.bind(this)
		this.headerMediaQuery = window.matchMedia('(max-width: 990px)')
		this.headerMediaQuery.addEventListener(
			'change',
			this.setHeaderHeightHandler
		)

		this.headerResizeObserver = new ResizeObserver(() => {
			this.setHeaderHeight()
		})
		this.headerResizeObserver.observe(this.header)

		if (this.headerIsAlwaysSticky && this.header) {
			this.header.classList.add('shopify-section-header-sticky')
		}

		this.currentScrollTop = 0
		this.preventReveal = false
		this.predictiveSearch = q.os<PredictiveSearch>(
			'predictive-search',
			this
		)

		this.onScrollHandler = this.onScroll.bind(this)
		this.hideHeaderOnScrollUp = () => (this.preventReveal = true)

		this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp)
		window.addEventListener('scroll', this.onScrollHandler, false)

		this.createObserver()
	}

	setHeaderHeight() {
		if (!this.header) throw new Error('no header element found')
		const viewport = window.innerWidth >= 750 ? 'desktop' : 'mobile'
		const headerHeight = `calc(${scaleValue(this.header.offsetHeight, viewport)} * var(--ax))`
		document.documentElement.style.setProperty(
			'--header-height',
			headerHeight
		)
		if (this.hasAttribute('data-homepage-overlap-enabled')) {
			document.documentElement.style.setProperty(
				'--homepage-header-overlap-active',
				headerHeight
			)
		}
	}

	override disconnectedCallback() {
		if (this.headerMediaQuery && this.setHeaderHeightHandler) {
			this.headerMediaQuery.removeEventListener(
				'change',
				this.setHeaderHeightHandler
			)
		}
		this.headerResizeObserver?.disconnect()
		this.removeEventListener(
			'preventHeaderReveal',
			this.hideHeaderOnScrollUp
		)
		window.removeEventListener('scroll', this.onScrollHandler)
	}

	createObserver() {
		let observer = new IntersectionObserver((entries, observer) => {
			this.headerBounds = entries[0].intersectionRect
			observer.disconnect()
		})

		observer.observe(this.header)
	}

	onScroll() {
		const scrollTop =
			window.pageYOffset || document.documentElement.scrollTop

		if (this.predictiveSearch && this.predictiveSearch.isOpen) return

		if (!(this.headerBounds instanceof DOMRectReadOnly)) return

		if (scrollTop > this.headerBounds.bottom) {
			this.header.classList.add('scrolled-past-header')
			this.classList.remove('gradient')
			requestAnimationFrame(this.reveal.bind(this))
		} else {
			this.header.classList.remove('scrolled-past-header')
			this.classList.add('gradient')
			requestAnimationFrame(this.reset.bind(this))
		}

		this.currentScrollTop = scrollTop
	}

	hide() {
		if (this.headerIsAlwaysSticky) return
		this.header.classList.add(
			'shopify-section-header-hidden',
			'shopify-section-header-sticky'
		)
		this.closeMenuDisclosure()
		this.closeSearchModal()
	}

	reveal() {
		if (this.headerIsAlwaysSticky) return
		this.header.classList.add('shopify-section-header-sticky', 'animate')
		this.header.classList.remove('shopify-section-header-hidden')
	}

	reset() {
		if (this.headerIsAlwaysSticky) return
		this.header.classList.remove(
			'shopify-section-header-hidden',
			'shopify-section-header-sticky',
			'animate'
		)
	}

	closeMenuDisclosure() {
		this.disclosures =
			this.disclosures ||
			q.ol<HeaderMenu>('header-menu', this.header)
		this.disclosures?.forEach((disclosure) => {
			disclosure.close()
		})
	}

	closeSearchModal() {
		this.searchModal =
			this.searchModal ??
			q.os<DetailsModal>('details-modal', this.header)
		this.searchModal?.close(false)
	}
}
